/**
 * Uptime funcional de las APIs de proveedores (CP-384): CMF, Coelsa, Bind PSP,
 * Bind Banco. Lo mide la Lambda `proveedores` cada 5 min sobre los logs de los
 * connectors (sin llamar a los bancos) y billing lo sirve:
 *   GET /metrics/proveedores                → estado actual por proveedor y operación
 *   GET /metrics/proveedores/history?days=N → uptime diario y episodios de caída
 */
import { authFetch, authHeader, jsonOrThrow } from "./monitoring";

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  "http://localhost:3000";

export type EstadoOp = "sano" | "degradado" | "caido" | "sin_trafico";
export type Criticidad = "crítica" | "alta" | "media";

export interface ResumenVentanas {
  n: number; ok: number; error_proveedor: number; limite: number; pedido_invalido: number;
  timeout_nuestro: number; ventanas: number; caido: number; degradado: number;
  p95?: number | null; disponibilidad?: number | null;
}

export interface OperacionEstado {
  op: string; label: string; criticidad: Criticidad; consumidor: string; si_falla: string; sana: string;
  p95_ref: number; estado: EstadoOp; desde: string | null;
  ult15: ResumenVentanas; ult24: ResumenVentanas;
  ultima_ventana: string | null; ultimos_codigos: Record<string, number>;
}

export interface ProveedorEstado {
  key: string; label: string; fuente: string; nota: string; estado: EstadoOp; operaciones: OperacionEstado[];
}

export interface ProveedoresEstado {
  generado: string;
  ultima_corrida: { ts: string; desde: string; hasta: string; llamadas: number; errores: string[] } | null;
  proveedores: ProveedorEstado[];
}

export interface EventoCaida {
  desde: number; hasta: number; desde_iso: string; hasta_iso: string; duracion_s: number; ventanas: number;
  errores: number; llamadas: number; codigos: Record<string, number>; detalles: string[]; en_curso?: boolean;
}

export interface OperacionHistorial {
  prov: string; op: string; label: string; criticidad: Criticidad; uptime: number | null; llamadas: number;
  errores_proveedor: number; minutos_caido: number;
  dias: { d: string; uptime: number | null; n: number; caido: number; degradado: number; ventanas: number }[];
  eventos: EventoCaida[];
}

export interface ProveedoresHistorial { generado: string; dias: number; operaciones: OperacionHistorial[] }

export async function getProveedoresEstado(): Promise<ProveedoresEstado> {
  const res = await authFetch(`${API_BASE}/metrics/proveedores`, { headers: authHeader() });
  return jsonOrThrow(res, "proveedores");
}

export async function getProveedoresHistorial(days = 30): Promise<ProveedoresHistorial> {
  const res = await authFetch(`${API_BASE}/metrics/proveedores/history?days=${days}`, { headers: authHeader() });
  return jsonOrThrow(res, "proveedores-history");
}

/** Texto plano con los episodios de una operación, para pegar en el reclamo al proveedor. */
export function evidenciaTexto(o: OperacionHistorial, dias: number): string {
  const L = [`${o.prov.toUpperCase()} · ${o.label} (${o.op}) — últimos ${dias} días`,
             `Disponibilidad: ${o.uptime == null ? "sin datos" : (o.uptime * 100).toFixed(2) + " %"} · ${o.llamadas} llamadas · ${o.errores_proveedor} errores del proveedor · ${o.minutos_caido} min caído`,
             ""];
  if (!o.eventos.length) L.push("Sin episodios de caída.");
  for (const e of o.eventos) {
    const cods = Object.entries(e.codigos).map(([k, v]) => `${k}×${v}`).join(", ");
    L.push(`- ${e.desde_iso} → ${e.hasta_iso} UTC (${Math.round(e.duracion_s / 60)} min): ${e.errores} errores de ${e.llamadas} llamadas${cods ? ` [${cods}]` : ""}${e.en_curso ? " (en curso)" : ""}`);
    for (const d of e.detalles.slice(0, 2)) L.push(`    ${d}`);
  }
  L.push("", "Fuente: logs de los connectors de Cucuru (medición pasiva, ventanas de 5 min). Caído = ≥ 50 % de errores del proveedor en la ventana.");
  return L.join("\n");
}
